// Phase 2D.1B / 2D.2B follow-up — where an authenticated user belongs when
// nothing more specific (an invitation continuation, a direct URL) claims
// them: after Login, on "/", and on any unmatched path.
//
// A platform super administrator may legitimately have ZERO organization
// membership — that is the whole point of PlatformAccessGate not requiring
// one. Before this fix, every one of those landing spots assumed "zero
// organizations" meant "unfinished tenant signup" and sent such an admin into
// /auth/confirm-signup, which they can never finish and were never meant to
// see.
//
// Platform access is NEVER inferred from email, metadata or frontend state —
// only from the backend-authoritative `usePlatformAccess()` result, the same
// one PlatformAccessGate itself trusts (`GET /api/platform-admin/access`).

export const LANDING = Object.freeze({
  LOGIN: "/login",
  DASHBOARD: "/dashboard",
  PLATFORM_ADMIN: "/platform-admin",
  CONFIRM_SIGNUP: "/auth/confirm-signup",
});

/**
 * Pure decision function — no hooks, no I/O — so the routing rule itself is
 * unit-testable without rendering a component tree.
 *
 * Returns one of the fixed `LANDING` paths, or `null` when there isn't yet
 * enough information to decide (still loading), in which case callers keep
 * showing a waiting state rather than guessing.
 */
export function resolveAuthenticatedLanding({
  session,
  orgsLoaded,
  orgs,
  platformAccessLoading,
  isPlatformAdmin,
}) {
  if (!session) return LANDING.LOGIN;
  if (!orgsLoaded) return null;
  if ((orgs?.length || 0) > 0) return LANDING.DASHBOARD;
  // Zero organizations: this is either an admin console with no tenant, or an
  // unfinished tenant signup. Only a VERIFIED platform admin skips onboarding.
  // Fail closed, exactly like usePlatformAccess itself: anything other than
  // an explicit `true` (a stray truthy value, a hostile caller) is no access.
  if (platformAccessLoading) return null;
  return isPlatformAdmin === true ? LANDING.PLATFORM_ADMIN : LANDING.CONFIRM_SIGNUP;
}
